/**
 * Canonical filter options for atmosphere and accessibility
 * These must match across frontend, backend, and database
 */

export interface FilterOption {
  id: string;
  label: string;
  icon: string;
}

/**
 * Atmosphere filter options
 * IDs use lowercase with underscores (e.g., family_friendly)
 * Backend will normalize incoming values with .toLowerCase().replace(/-/g, '_')
 */
export const ATMOSPHERE_OPTIONS: FilterOption[] = [
  { id: 'casual', label: 'Casual', icon: '👕' },
  { id: 'upscale', label: 'Upscale', icon: '🍷' },
  { id: 'romantic', label: 'Romantic', icon: '💕' },
  { id: 'family_friendly', label: 'Family Friendly', icon: '👨‍👩‍👧' },
  { id: 'quiet', label: 'Quiet', icon: '🤫' },
  { id: 'lively', label: 'Lively', icon: '🎉' },
  { id: 'outdoor_seating', label: 'Outdoor Seating', icon: '🌳' },
  { id: 'cozy', label: 'Cozy', icon: '🛋️' },
  { id: 'modern', label: 'Modern', icon: '🏢' },
  { id: 'traditional', label: 'Traditional', icon: '🏛️' },
];

/**
 * Accessibility filter options
 * IDs use lowercase with underscores (e.g., wheelchair_accessible)
 * Uses Google Places API accessibilityOptions field for filtering
 */
export const ACCESSIBILITY_OPTIONS: FilterOption[] = [
  { id: 'wheelchair_accessible', label: 'Wheelchair Accessible', icon: '♿' },
];

/**
 * Extended accessibility options for user preferences (onboarding)
 * These are user preference settings, not necessarily Google Places API fields
 */
export const ACCESSIBILITY_OPTIONS_EXTENDED: FilterOption[] = [
  { id: 'wheelchair_accessible', label: 'Wheelchair Accessible', icon: '♿' },
  { id: 'hearing_assistance', label: 'Hearing Assistance', icon: '🦻' },
  { id: 'visual_assistance', label: 'Visual Assistance', icon: '👁️' },
  { id: 'service_animals', label: 'Service Animals Welcome', icon: '🐕‍🦺' },
  { id: 'accessible_parking', label: 'Accessible Parking', icon: '🅿️' },
  { id: 'elevator_access', label: 'Elevator Access', icon: '🛗' },
];

/**
 * Dietary restriction filter options
 * IDs use lowercase with underscores (e.g., gluten_free)
 */
export const DIETARY_OPTIONS: FilterOption[] = [
  { id: 'vegetarian', label: 'Vegetarian', icon: '🥬' },
  { id: 'vegan', label: 'Vegan', icon: '🌱' },
  { id: 'gluten_free', label: 'Gluten-free', icon: '🌾' },
  { id: 'halal', label: 'Halal', icon: '☪️' },
  { id: 'kosher', label: 'Kosher', icon: '✡️' },
  { id: 'nut_free', label: 'Nut-free', icon: '🥜' },
  { id: 'dairy_free', label: 'Dairy-free', icon: '🥛' },
  { id: 'low_carb', label: 'Low-carb', icon: '🥩' },
  { id: 'keto', label: 'Keto', icon: '🥑' },
];

/**
 * Helper function to normalize user input to match our canonical IDs
 * Matches backend normalization: .toLowerCase().replace(/-/g, '_')
 */
export const normalizeFilterId = (value: string): string => {
  return value.toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_');
};

/**
 * Helper function to find an option by normalized ID
 */
export const findAtmosphereOption = (id: string): FilterOption | undefined => {
  const normalizedId = normalizeFilterId(id);
  return ATMOSPHERE_OPTIONS.find(opt => normalizeFilterId(opt.id) === normalizedId);
};

export const findAccessibilityOption = (id: string): FilterOption | undefined => {
  const normalizedId = normalizeFilterId(id);
  return ACCESSIBILITY_OPTIONS.find(opt => normalizeFilterId(opt.id) === normalizedId);
};

export const findDietaryOption = (id: string): FilterOption | undefined => {
  const normalizedId = normalizeFilterId(id);
  return DIETARY_OPTIONS.find(opt => normalizeFilterId(opt.id) === normalizedId);
};
